import React, { useState, useEffect } from 'react';
import StorageManager from '../services/StorageManager';

function AccountPage({ currentUser, onBack, onNavigate, onLogout }) {
  const [votingHistory, setVotingHistory] = useState([]);
  const [sentMessages, setSentMessages] = useState([]);
  const [stats, setStats] = useState({ pilotsVoted: 0, genresExplored: 0, monthsActive: 1 });
  const [notifications, setNotifications] = useState({ newPilots: true, weeklyDigest: false });
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [hoveredNav, setHoveredNav] = useState(null);

  useEffect(() => {
    const loadData = async () => {
      if (!currentUser) return;
      try {
        const votes = await StorageManager.getVoterVotes(currentUser.id);
        const allPilots = await StorageManager.getPilots();
        const history = votes.slice(0, 5).map(v => {
          const pid = Array.isArray(v.pilotId) ? v.pilotId[0] : v.pilotId;
          const pilot = allPilots.find(p => p.id === pid);
          return { ...v, pilot };
        }).filter(v => v.pilot);
        setVotingHistory(history);

        const genres = new Set(votes.map(v => {
          const pid = Array.isArray(v.pilotId) ? v.pilotId[0] : v.pilotId;
          return allPilots.find(p => p.id === pid)?.genre;
        }).filter(Boolean));
        const memberSince = currentUser.createdAt ? new Date(currentUser.createdAt) : new Date();
        const monthsActive = Math.max(1, Math.ceil((new Date() - memberSince) / (1000 * 60 * 60 * 24 * 30)));
        setStats({ pilotsVoted: votes.length, genresExplored: genres.size || 0, monthsActive });

        // Load sent messages
        try {
          const msgs = await StorageManager.getMessagesSentByUser(currentUser.id);
          setSentMessages(msgs || []);
        } catch (e) { console.log('Messages not available:', e); }
      } catch (e) { console.error('Error loading account data:', e); }
    };
    loadData();
  }, [currentUser]);

  const navStyle = (id) => ({
    background: 'none', border: 'none', cursor: 'pointer',
    color: hoveredNav === id ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '0.85rem', fontWeight: 400, fontFamily: "'Cormorant Garamond', Georgia, serif",
    transition: 'color 0.25s ease', letterSpacing: '0.04em', padding: '0.3rem 0'
  });

  const sectionStyle = {
    background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)',
    borderRadius: '12px', padding: '1.5rem', marginBottom: '1.5rem'
  };

  const ghostBtn = (id, disabled) => ({
    background: 'none', border: '1px solid rgba(255,255,255,0.12)', borderRadius: '10px',
    padding: '0.6rem 1.5rem', color: hoveredNav === id ? 'rgba(255,255,255,0.85)' : 'rgba(255,255,255,0.45)',
    fontSize: '0.85rem', fontWeight: 500, fontFamily: "'Cormorant Garamond', Georgia, serif",
    cursor: disabled ? 'not-allowed' : 'pointer', transition: 'all 0.25s ease', letterSpacing: '0.04em',
    borderColor: hoveredNav === id ? 'rgba(255,255,255,0.25)' : 'rgba(255,255,255,0.12)'
  });

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', background: '#000000', fontFamily: "'Cormorant Garamond', Georgia, serif", color: '#fff' }}>
      <style dangerouslySetInnerHTML={{ __html: `
        @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:wght@300;400;500;600&display=swap');
        @keyframes fadeInUp { from { opacity: 0; transform: translateY(16px); } to { opacity: 1; transform: translateY(0); } }
      ` }} />

      {/* Header */}
      <header style={{ padding: '1rem 1.5rem', display: 'flex', alignItems: 'center', justifyContent: 'space-between', borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', cursor: 'pointer' }} onClick={() => onNavigate('browse')}>
          <span style={{ fontSize: '1rem', fontWeight: 500, letterSpacing: '0.25em', textTransform: 'uppercase', background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>Pilot Light</span>
        </div>
        <nav style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
          <button onClick={() => onNavigate('browse')} style={navStyle('home')}
            onMouseEnter={() => setHoveredNav('home')} onMouseLeave={() => setHoveredNav(null)}>Screening Room</button>
          <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.85rem', fontWeight: 500 }}>Account</span>
        </nav>
      </header>

      <div style={{ flex: 1, padding: '2rem 1.5rem', maxWidth: '700px', margin: '0 auto', width: '100%', boxSizing: 'border-box', animation: 'fadeInUp 0.5s ease-out' }}>

        {/* Profile */}
        <div style={{ marginBottom: '2rem', textAlign: 'center' }}>
          <div style={{ width: '56px', height: '56px', borderRadius: '50%', margin: '0 auto 0.8rem',
            background: 'linear-gradient(135deg, rgba(255,107,107,0.3), rgba(254,202,87,0.3), rgba(78,205,196,0.3))',
            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.3rem', border: '1px solid rgba(255,255,255,0.1)' }}>
            {(currentUser?.name || currentUser?.email || '?')[0].toUpperCase()}
          </div>
          <h2 style={{ fontSize: '1.2rem', fontWeight: 500, marginBottom: '0.2rem', color: 'rgba(255,255,255,0.8)', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>{currentUser?.name || currentUser?.displayName || 'Member'}</h2>
          <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.8rem', margin: 0 }}>{currentUser?.email}</p>
          <p style={{ color: 'rgba(255,255,255,0.2)', fontSize: '0.72rem', margin: '0.3rem 0 0' }}>
            Member since {currentUser?.createdAt ? new Date(currentUser.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' }) : '2026'}
          </p>
        </div>

        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.8rem', marginBottom: '2rem' }}>
          {[
            { n: stats.pilotsVoted, label: 'Reviewed' },
            { n: stats.genresExplored, label: 'Genres' },
            { n: stats.monthsActive, label: 'Months' }
          ].map((s, i) => (
            <div key={i} style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '10px', padding: '1rem', textAlign: 'center' }}>
              <div style={{ fontSize: '1.5rem', fontWeight: 600, marginBottom: '0.15rem',
                background: 'linear-gradient(135deg, #ff6b6b 0%, #feca57 50%, #4ecdc4 100%)',
                WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{s.n}</div>
              <div style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.75rem', letterSpacing: '0.04em' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Recent Votes */}
        <div style={sectionStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 500, color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', letterSpacing: '0.04em', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Recent Reviews</h3>
          {votingHistory.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {votingHistory.map((item, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.6rem 0',
                  borderBottom: i < votingHistory.length - 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
                  <div>
                    <span style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)' }}>{item.pilot?.pilotTitle || 'Unknown'}</span>
                    <span style={{ color: 'rgba(255,255,255,0.25)', fontSize: '0.72rem', marginLeft: '0.5rem' }}>
                      {item.createdAt ? new Date(item.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''}
                    </span>
                  </div>
                  <span style={{ color: '#feca57', fontSize: '0.82rem', fontWeight: 500 }}>★ {item.overallScore || '—'}</span>
                </div>
              ))}
            </div>
          ) : (
            <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.82rem', textAlign: 'center' }}>No reviews yet.</p>
          )}
        </div>

        {/* Comments left on pilots */}
        {sentMessages.length > 0 && (
          <div style={sectionStyle}>
            <h3 style={{ fontSize: '0.95rem', fontWeight: 500, color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', letterSpacing: '0.04em', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Your Comments</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {sentMessages.map((msg, i) => (
                <div key={i} style={{ padding: '0.8rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.05)' }}>
                  <div style={{ marginBottom: '0.3rem' }}>
                    <span style={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.6)' }}>{msg.pilotTitle || 'Pilot'}</span>
                  </div>
                  <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.78rem', margin: 0, lineHeight: 1.5 }}>{msg.messageText}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Notifications */}
        <div style={sectionStyle}>
          <h3 style={{ fontSize: '0.95rem', fontWeight: 500, color: 'rgba(255,255,255,0.6)', marginBottom: '1rem', letterSpacing: '0.04em', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Notifications</h3>
          {[
            { key: 'newPilots', title: 'New pilot alerts', desc: 'When new pilots are added to the season' },
            { key: 'weeklyDigest', title: 'Weekly digest', desc: 'Weekly summary of activity' }
          ].map((item, i) => (
            <div key={item.key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.8rem 0',
              borderBottom: i < 1 ? '1px solid rgba(255,255,255,0.05)' : 'none' }}>
              <div>
                <span style={{ fontSize: '0.88rem', color: 'rgba(255,255,255,0.7)' }}>{item.title}</span>
                <p style={{ color: 'rgba(255,255,255,0.3)', fontSize: '0.75rem', margin: '0.15rem 0 0' }}>{item.desc}</p>
              </div>
              <div onClick={() => setNotifications({ ...notifications, [item.key]: !notifications[item.key] })}
                style={{ width: '40px', height: '22px', background: notifications[item.key] ? '#4ecdc4' : 'rgba(255,255,255,0.15)',
                  borderRadius: '11px', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                <div style={{ position: 'absolute', width: '18px', height: '18px', background: '#fff', borderRadius: '50%',
                  top: '2px', left: notifications[item.key] ? '20px' : '2px', transition: 'left 0.2s' }} />
              </div>
            </div>
          ))}
        </div>

        {/* Session */}
        <div style={{ display: 'flex', gap: '0.8rem', marginBottom: '1.5rem' }}>
          <button onClick={onLogout} style={ghostBtn('logout', false)}
            onMouseEnter={() => setHoveredNav('logout')} onMouseLeave={() => setHoveredNav(null)}>Log Out</button>
        </div>

        {/* Delete */}
        <button onClick={() => setShowDeleteConfirm(true)}
          style={{ background: 'none', border: 'none', color: 'rgba(255,107,107,0.4)', fontSize: '0.75rem',
            cursor: 'pointer', fontFamily: "'Cormorant Garamond', Georgia, serif", padding: 0, transition: 'color 0.2s' }}
          onMouseOver={e => e.currentTarget.style.color = '#ff6b6b'}
          onMouseOut={e => e.currentTarget.style.color = 'rgba(255,107,107,0.4)'}>Delete Account</button>
      </div>

      {/* Footer */}
      <footer style={{ padding: '2.5rem 2rem', textAlign: 'center', marginTop: 'auto' }}>
        <p style={{ color: 'rgba(255,255,255,0.15)', fontSize: '0.75rem', margin: 0, letterSpacing: '0.04em' }}>
          © 2026 Pilot Light. All rights reserved.
        </p>
      </footer>

      {/* Delete Modal */}
      {showDeleteConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '1rem' }}>
          <div style={{ background: '#000000', borderRadius: '12px', padding: '2rem', maxWidth: '420px', width: '100%', boxSizing: 'border-box', border: '1px solid rgba(255,107,107,0.2)', fontFamily: "'Cormorant Garamond', Georgia, serif" }}>
            <h3 style={{ color: '#ff6b6b', marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 500, fontFamily: "'Cormorant Garamond', Georgia, serif" }}>Delete Account?</h3>
            <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '1.2rem', lineHeight: 1.6, fontSize: '0.85rem' }}>
              This action is permanent. All your data and voting history will be deleted.
            </p>
            <p style={{ color: 'rgba(255,255,255,0.5)', marginBottom: '0.5rem', fontSize: '0.82rem' }}>
              Type <strong style={{ color: 'rgba(255,255,255,0.8)' }}>DELETE</strong> to confirm:
            </p>
            <input type="text" value={deleteConfirmText} onChange={e => setDeleteConfirmText(e.target.value)}
              placeholder="Type DELETE"
              style={{ width: '100%', padding: '0.7rem', borderRadius: '8px', border: '1px solid rgba(255,255,255,0.1)',
                background: 'rgba(255,255,255,0.05)', color: '#fff', fontSize: '0.88rem', boxSizing: 'border-box',
                outline: 'none', marginBottom: '1.2rem', fontFamily: "'Cormorant Garamond', Georgia, serif" }} />
            <div style={{ display: 'flex', gap: '0.8rem', justifyContent: 'flex-end' }}>
              <button onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmText(''); }}
                style={ghostBtn('cancel-delete', false)}
                onMouseEnter={() => setHoveredNav('cancel-delete')} onMouseLeave={() => setHoveredNav(null)}>Cancel</button>
              <button onClick={async () => {
                if (deleteConfirmText === 'DELETE' && currentUser?.id) {
                  const result = await StorageManager.softDeleteAccount(currentUser.id);
                  if (result.success) { alert('Account deleted.'); onLogout(); }
                  else { alert(result.message || 'Failed to delete account.'); }
                }
              }}
                disabled={deleteConfirmText !== 'DELETE'}
                style={{ padding: '0.6rem 1.5rem', borderRadius: '10px', fontSize: '0.85rem', fontWeight: 500,
                  background: deleteConfirmText === 'DELETE' ? '#ff6b6b' : 'rgba(255,107,107,0.2)',
                  border: 'none', color: deleteConfirmText === 'DELETE' ? '#fff' : 'rgba(255,255,255,0.3)',
                  cursor: deleteConfirmText === 'DELETE' ? 'pointer' : 'not-allowed' }}>Delete</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default AccountPage;
